// src/components/about/About.jsx
import React, { useState, useEffect, useRef } from 'react';

import './about.css';

// ✅ FIXED: Added isClient prop to prevent duplicate "Meet Your Developer" title
const About = ({ isClient = false }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const achievements = [
    { number: '25+', label: 'Projects Completed', icon: 'bx-folder-open' },
    { number: '10+', label: 'Happy Clients', icon: 'bx-user-check' },
    { number: '5+', label: 'Years Experience', icon: 'bx-time-five' },
    { number: '99%', label: 'Client Satisfaction', icon: 'bx-star' }
  ];

  const expertise = [
    { 
      title: 'Full-Stack Development', 
      icon: 'bx-code-alt',
      description: 'End-to-end web application development with modern technologies'
    },
    { 
      title: 'Cloud Structure', 
      icon: 'bx-cloud',
      description: 'Scalable solutions on AWS, Google Cloud, and Azure, Vercel, Railway'
    },
    { 
      title: 'Performance Optimization', 
      icon: 'bx-tachometer',
      description: 'Speed and efficiency improvements for web applications'
    },
    { 
      title: 'API Development', 
      icon: 'bx-plug',
      description: 'RESTful and GraphQL API design and implementation'
    }
  ];

  const tabContent = {
    summary: {
      title: 'Professional Summary',
      content: (
        <>
          <p className="about__description">
         I'm a full-stack software engineer based in Brooklyn, building production web applications across the JavaScript and .NET ecosystems. For more than five years I've shipped real systems end to end React front ends, Node and ASP.NET Core back ends, and the databases and integrations that hold them together for everything from local businesses to a property-tech platform that scanned listings nationwide.
My background runs deeper than building from scratch.
          </p>
          <p className="about__description">
            I've stabilized a live Node.js service under load, traced and patched crashes that were breaking jobs mid-run, designed and tuned PostgreSQL and SQL Server schemas, and wired up the third-party tools businesses actually rely on Stripe, HubSpot, Wix, Zapier, and Meta. That mix of frontend polish and backend reliability is what takes a project from an idea to something that holds up in the real world.
          </p>
          <p className="about__description">
            I pair technical execution with a clear focus on the business outcome  whether that's a restaurant taking orders without losing 30% to delivery apps, a small business automating the busywork that eats its week, or a custom API that ties a company's systems together. Clean code, honest timelines, and direct communication you work with the developer who's actually building it, not an agency middleman.
          </p>
          
        </>
      )
    },
    approach: {
      title: 'My Approach',
      content: (
        <>
          <p className="about__description">
            I follow a structured yet flexible approach to every project, 
            ensuring quality and timely delivery.
          </p>
          <div className="approach-steps">
            <div className="approach-step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h4>Discover</h4>
                <p>Understanding your business goals and requirements</p>
              </div>
            </div>
            <div className="approach-step">
              <div className="step-number">02</div>
              <div className="step-content">
                <h4>Plan</h4>
                <p>Create a detailed roadmap with clear milestones</p>
              </div>
            </div>
            <div className="approach-step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h4>Build</h4>
                <p>Develop with constant communication and updates</p>
              </div>
            </div>
            <div className="approach-step">
              <div className="step-number">04</div>
              <div className="step-content">
                <h4>Launch & Support</h4>
                <p>Deploy smoothly and provide ongoing maintenance</p>
              </div>
            </div>
          </div>
        </>
      )
    },
    education: {
      title: 'Education & Certifications',
      content: (
        <>
          <div className="education__list">
            <div className="education-item">
              <div className="education-icon">
                <i className='bx bx-certification'></i>
              </div>
              <div className="education-content">
                <h4>Bachelor of Computer Science</h4>
                <p>Brooklyn College • 2026</p>
              </div>
            </div>
            <div className="education-item">
              <div className="education-icon">
                <i className='bx bx-certification'></i>
              </div>
              <div className="education-content">
                <h4>AOS in Computer Programming & IT</h4>
                <p>ASA College • 2020</p>
              </div>
            </div>
            <div className="education-item">
              <div className="education-icon">
                <i className='bx bx-certification'></i>
              </div>
              <div className="education-content">
                <h4>CompTIA Security+</h4>
                <p>• Present</p>
              </div>
            </div>
          </div>
        </>
      )
    }
  };

  // If isClient is true, don't render the outer section (parent handles it)
  // If isClient is false, render everything including the section wrapper
  
  const renderContent = () => (
    <>
      <div className="about__container container">
        {/* Left Side - Image and Quick Info */}
        <div className={`about__left ${isVisible ? 'animate-in' : ''}`}>
          {/* Profile Card */}
          <div className="about__profile-card">
            <img
  src="/spinoza-delva-full-stack-developer.jpg"
  alt="Spinoza Delva — Full-Stack Developer"
  className="about__profile-pic"
/>
            <h3 className="about__name">Spinoza Delva</h3>
            <span className="about__subtitle">Full-Stack Developer</span>
            <p className="about__description">
              A passionate developer with a knack for crafting scalable web solutions.
              I love turning complex problems into elegant, user-friendly experiences.
            </p>
            <div className="about__buttons">
              <a href="/#contact" className="button button--flex">
                Hire Me
                <i className="uil uil-arrow-right button__icon"></i>
              </a>
              <a href="/assets/Spinoza_Delva_Resume.pdf" download className="button button--outline button--flex">
                Download CV
                <i className="uil uil-download-alt button__icon"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Right Side - Tabbed Content */}
        <div className={`about__right ${isVisible ? 'animate-in' : ''}`}>
          {/* Tab Navigation */}
          <div className="about__tabs">
            {Object.keys(tabContent).map((key) => (
              <button
                key={key}
                className={`about__tab ${activeTab === key ? 'active' : ''}`}
                onClick={() => setActiveTab(key)}
              >
                {tabContent[key].title}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="about__tab-content">
            {tabContent[activeTab].content}
          </div>
        </div>
      </div>

      {/* Achievements Section */}
      <div className="about__achievements">
        <div className="achievements__container container">
          {achievements.map((achievement, index) => (
            <div 
              key={index} 
              className={`achievement-card ${isVisible ? 'animate-in' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <i className={`bx ${achievement.icon} achievement-icon`}></i>
              <h3>{achievement.number}</h3>
              <p>{achievement.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Expertise Section */}
      <div className="about__expertise">
        <h3 className="expertise__title">Why Choose Me</h3>
        <div className="expertise__container container">
          {expertise.map((item, index) => (
            <div 
              key={index} 
              className={`expertise-card ${isVisible ? 'animate-in' : ''}`}
              style={{ animationDelay: `${index * 0.1 + 0.4}s` }}
            >
              <i className={`bx ${item.icon} expertise-icon`}></i>
              <h4>{item.title}</h4>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  // If isClient, just return the content (parent provides the section wrapper)
  if (isClient) {
    return <div ref={sectionRef}>{renderContent()}</div>;
  }

  // Otherwise, return full section with title (non-logged in users see "About Me")
  return (
    <section className="about section" id="about" ref={sectionRef}>
      <h2 className="section__title">About Me</h2>
      <span className="section__subtitle">My professional journey</span>
      {renderContent()}
    </section>
  );
};

export default About;